import { useCallback, useEffect } from 'react';
import { useEvaluationHistory } from './evaluationHistoryAtoms';
import { useUndoEvaluation } from './useUndoEvaluation';
import { useRedoEvaluation } from './useRedoEvaluation';

/**
 * Custom hook to handle undo/redo functionality for evaluation forms
 *
 * @param carnetDeNotesId - The ID of the carnet de notes
 * @param evaluationId - The ID of the evaluation
 * @returns Object with undo/redo handlers and state
 */
export function useEvaluationUndoRedo(
  carnetDeNotesId: string,
  evaluationId: string,
) {
  const { isPastEmpty, isFutureEmpty } = useEvaluationHistory(evaluationId);
  const handleUndo = useUndoEvaluation(carnetDeNotesId, evaluationId);
  const handleRedo = useRedoEvaluation(carnetDeNotesId, evaluationId);

  // Keyboard shortcuts for undo/redo
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Undo: Ctrl+Z or Cmd+Z
      if (
        (e.ctrlKey || e.metaKey) &&
        e.key === 'z' &&
        !e.shiftKey &&
        !isPastEmpty
      ) {
        e.preventDefault();
        handleUndo();
      }
      // Redo: Ctrl+Shift+Z or Cmd+Shift+Z or Ctrl+Y or Cmd+Y
      if (
        ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) ||
        ((e.ctrlKey || e.metaKey) && e.key === 'y')
      ) {
        if (!isFutureEmpty) {
          e.preventDefault();
          handleRedo();
        }
      }
    },
    [handleUndo, handleRedo, isPastEmpty, isFutureEmpty],
  );

  // Add event listener for keyboard shortcuts
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return {
    handleUndo,
    handleRedo,
    canUndo: !isPastEmpty,
    canRedo: !isFutureEmpty,
  };
}
